import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus as AddIcon, Pencil as EditIcon, CheckCircle as CompleteIcon } from 'lucide-react';
import { Button, Chip, ShellHeader } from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import EmptyState from '../components/data-display/EmptyState';
import { InvestigationStepForm } from '../components/investigations/InvestigationStepForm';
import { investigationService } from '../services/investigationService';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';
import {
  IncidentSeverities,
  InvestigationStatuses,
  InvestigationStepTypes,
  InvestigationStepStatuses,
  MessageSenderTypes,
  type IncidentInvestigationSessionDto,
  type IncidentInvestigationStepDto,
  type IncidentInvestigationMessageDto,
  type IncidentCorrelationDto,
  type InvestigationStepStatus,
  type InvestigationStepType,
} from '../types/investigation';

const severityLabel = (severity: number): string => {
  switch (severity) {
    case IncidentSeverities.Critical:
      return 'Critical';
    case IncidentSeverities.High:
      return 'High';
    case IncidentSeverities.Medium:
      return 'Medium';
    case IncidentSeverities.Low:
      return 'Low';
    default:
      return 'Unknown';
  }
};

const severityColor = (severity: number): 'default' | 'green' | 'blue' | 'amber' | 'red' => {
  switch (severity) {
    case IncidentSeverities.Critical:
      return 'red';
    case IncidentSeverities.High:
      return 'amber';
    case IncidentSeverities.Medium:
      return 'blue';
    case IncidentSeverities.Low:
      return 'green';
    default:
      return 'default';
  }
};

const statusLabel = (status: number): string => {
  switch (status) {
    case InvestigationStatuses.Completed:
      return 'Completed';
    case InvestigationStatuses.InProgress:
      return 'In Progress';
    case InvestigationStatuses.Paused:
      return 'Paused';
    case InvestigationStatuses.Cancelled:
      return 'Cancelled';
    case InvestigationStatuses.NotStarted:
      return 'Not Started';
    default:
      return 'Unknown';
  }
};

const statusColor = (status: number): 'default' | 'green' | 'blue' | 'amber' | 'red' => {
  switch (status) {
    case InvestigationStatuses.Completed:
      return 'green';
    case InvestigationStatuses.InProgress:
      return 'blue';
    case InvestigationStatuses.Paused:
      return 'amber';
    case InvestigationStatuses.Cancelled:
      return 'red';
    default:
      return 'default';
  }
};

const stepTypeLabel = (type: InvestigationStepType): string => {
  switch (type) {
    case InvestigationStepTypes.DataCollection:
      return 'Data Collection';
    case InvestigationStepTypes.Analysis:
      return 'Analysis';
    case InvestigationStepTypes.Hypothesis:
      return 'Hypothesis';
    case InvestigationStepTypes.Verification:
      return 'Verification';
    case InvestigationStepTypes.Conclusion:
      return 'Conclusion';
    default:
      return 'Unknown';
  }
};

const stepStatusLabel = (status: InvestigationStepStatus): string => {
  switch (status) {
    case InvestigationStepStatuses.Pending:
      return 'Pending';
    case InvestigationStepStatuses.InProgress:
      return 'In Progress';
    case InvestigationStepStatuses.Completed:
      return 'Completed';
    case InvestigationStepStatuses.Skipped:
      return 'Skipped';
    case InvestigationStepStatuses.Failed:
      return 'Failed';
    default:
      return 'Unknown';
  }
};

const stepStatusColor = (status: InvestigationStepStatus): 'default' | 'green' | 'blue' | 'amber' | 'red' => {
  switch (status) {
    case InvestigationStepStatuses.Completed:
      return 'green';
    case InvestigationStepStatuses.InProgress:
      return 'blue';
    case InvestigationStepStatuses.Pending:
      return 'amber';
    case InvestigationStepStatuses.Failed:
      return 'red';
    default:
      return 'default';
  }
};

/**
 * Investigation detail page component (Module 24).
 */
export const InvestigationDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  const [investigation, setInvestigation] = useState<IncidentInvestigationSessionDto | null>(null);
  const [steps, setSteps] = useState<IncidentInvestigationStepDto[]>([]);
  const [messages, setMessages] = useState<IncidentInvestigationMessageDto[]>([]);
  const [correlations, setCorrelations] = useState<IncidentCorrelationDto[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addStepOpen, setAddStepOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const loadAll = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const inv = await investigationService.getInvestigation(id);
      setInvestigation(inv);

      const [s, m, c] = await Promise.all([
        investigationService.getInvestigationSteps(id),
        investigationService.getInvestigationMessages(id),
        investigationService.getInvestigationCorrelations(id),
      ]);
      setSteps(s);
      setMessages(m);
      setCorrelations(c);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to load investigation';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleComplete = async () => {
    if (!id) return;
    try {
      await investigationService.completeInvestigation(id);
      showNotification('Investigation completed successfully', 'success');
      await loadAll();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to complete investigation';
      showNotification(msg, 'error');
    }
  };

  const stepColumns: DataTableColumn<IncidentInvestigationStepDto>[] = useMemo(
    () => [
      {
        id: 'order',
        label: '#',
        minWidth: 60,
        render: (s) => <p className="text-sm">{s.order}</p>,
      },
      {
        id: 'name',
        label: 'Step',
        minWidth: 240,
        render: (s) => (
          <div>
            <p className="text-sm font-semibold">
              {s.stepName}
            </p>
            {s.description && (
              <span className="text-xs text-[var(--color-text-secondary)]">
                {s.description.length > 90 ? `${s.description.slice(0, 90)}…` : s.description}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'type',
        label: 'Type',
        minWidth: 150,
        render: (s) => <Chip variant="outlined" label={stepTypeLabel(s.stepType)} />,
      },
      {
        id: 'status',
        label: 'Status',
        minWidth: 140,
        render: (s) => <Chip color={stepStatusColor(s.status)} label={stepStatusLabel(s.status)} />,
      },
      {
        id: 'started',
        label: 'Started',
        minWidth: 170,
        render: (s) =>
          s.startedAt ? (
            <p className="text-sm">{new Date(s.startedAt).toLocaleString()}</p>
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)]">
              —
            </p>
          ),
      },
      {
        id: 'completed',
        label: 'Completed',
        minWidth: 170,
        render: (s) =>
          s.completedAt ? (
            <p className="text-sm">{new Date(s.completedAt).toLocaleString()}</p>
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)]">
              —
            </p>
          ),
      },
    ],
    []
  );

  const messageColumns: DataTableColumn<IncidentInvestigationMessageDto>[] = useMemo(
    () => [
      {
        id: 'seq',
        label: '#',
        minWidth: 60,
        render: (m) => <p className="text-sm">{m.sequenceNumber}</p>,
      },
      {
        id: 'sender',
        label: 'Sender',
        minWidth: 110,
        render: (m) => (
          <Chip
            variant="outlined"
            label={m.senderType === MessageSenderTypes.Agent ? 'Agent' : 'User'}
          />
        ),
      },
      {
        id: 'analysisType',
        label: 'Type',
        minWidth: 140,
        render: (m) => <p className="text-sm">{m.analysisType || '-'}</p>,
      },
      {
        id: 'content',
        label: 'Content',
        minWidth: 420,
        render: (m) => (
          <p className="text-sm whitespace-pre-wrap">
            {m.content}
          </p>
        ),
      },
      {
        id: 'createdAt',
        label: 'Created',
        minWidth: 180,
        render: (m) => <p className="text-sm">{new Date(m.createdAt).toLocaleString()}</p>,
      },
    ],
    []
  );

  const correlationColumns: DataTableColumn<IncidentCorrelationDto>[] = useMemo(
    () => [
      {
        id: 'type',
        label: 'Type',
        minWidth: 180,
        render: (c) => <p className="text-sm">{c.correlationType}</p>,
      },
      {
        id: 'entity',
        label: 'Related Entity',
        minWidth: 280,
        render: (c) => (
          <div>
            <p className="text-sm">{c.relatedEntityType || '-'}</p>
            <span className="text-xs text-[var(--color-text-secondary)] break-all">
              {c.relatedEntityId || '-'}
            </span>
          </div>
        ),
      },
      {
        id: 'confidence',
        label: 'Confidence',
        minWidth: 120,
        render: (c) => <p className="text-sm">{c.confidenceScore.toFixed(2)}</p>,
      },
      {
        id: 'reason',
        label: 'Reason',
        minWidth: 320,
        render: (c) => <p className="text-sm">{c.correlationReason || '-'}</p>,
      },
      {
        id: 'createdAt',
        label: 'Created',
        minWidth: 180,
        render: (c) => <p className="text-sm">{new Date(c.createdAt).toLocaleString()}</p>,
      },
    ],
    []
  );

  return (
    <>
      <ShellHeader
        title={investigation?.title || 'Investigation'}
        description={investigation ? `Agent: ${investigation.agentName}` : undefined}
        data-testid="investigation-header"
        actions={
          <>
            <Button variant="outline" onClick={() => navigate(ROUTES.INVESTIGATIONS)} data-testid="back-investigation-btn">
              Investigations
            </Button>
            {investigation && (
              <>
                <Button variant="outline" onClick={() => navigate(`/investigations/${investigation.id}/edit`)} data-testid="edit-investigation-btn">
                  <EditIcon /> Edit
                </Button>
                {investigation.investigationStatus !== InvestigationStatuses.Completed && (
                  <Button variant="default" onClick={handleComplete} data-testid="complete-investigation-btn">
                    <CompleteIcon /> Complete
                  </Button>
                )}
              </>
            )}
          </>
        }
      />

      <PageContainer data-testid="investigation-form-page">
        {error && (
          <InlineErrorPanel
            message={error}
            data-testid="investigation-error-panel"
            action={<Button variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>}
          />
        )}

        {loading && <LoadingSkeleton rows={6} data-testid="investigation-loading" />}

        {!loading && investigation && (
          <>
            <div className="flex flex-wrap gap-1 mb-3">
              <Chip color={statusColor(investigation.investigationStatus)} label={statusLabel(investigation.investigationStatus)} />
              <Chip color={severityColor(investigation.severity)} label={severityLabel(investigation.severity)} />
              {investigation.incidentNumber && (
                <Chip variant="outlined" label={`Incident: ${investigation.incidentNumber}`} />
              )}
            </div>

            <div className="page-detail-layout__tabs" role="tablist" aria-label="Investigation sections">
              {[
                { label: 'Overview', value: 'overview' },
                { label: `Steps (${steps.length})`, value: 'steps' },
                { label: `Messages (${messages.length})`, value: 'messages' },
                { label: `Correlations (${correlations.length})`, value: 'correlations' },
                { label: 'Findings', value: 'findings' },
              ].map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.value}
                  className="page-detail-layout__tab"
                  data-testid={`investigation-tab-${tab.value}`}
                  onClick={() => setActiveTab(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'overview' && (
              <FormSection title="Details" data-testid="investigation-section-details">
                <div className="grid gap-2">
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    Agent: {investigation.agentName}
                  </p>
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    Created: {new Date(investigation.createdAt).toLocaleString()}
                  </p>
                  {investigation.investigationStartedAt && (
                    <p className="text-sm text-[var(--color-text-secondary)]">
                      Started: {new Date(investigation.investigationStartedAt).toLocaleString()}
                    </p>
                  )}
                  {investigation.investigationCompletedAt && (
                    <p className="text-sm text-[var(--color-text-secondary)]">
                      Completed: {new Date(investigation.investigationCompletedAt).toLocaleString()}
                    </p>
                  )}
                  {investigation.description && (
                    <>
                      <p className="text-base font-semibold mt-2">
                        Description
                      </p>
                      <p className="text-sm whitespace-pre-wrap">
                        {investigation.description}
                      </p>
                    </>
                  )}
                </div>
              </FormSection>
            )}

            {activeTab === 'steps' && (
              <div>
                <div className="flex justify-end mb-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    data-testid="investigation-add-step-btn"
                    onClick={() => setAddStepOpen(true)}
                  >
                    <AddIcon size={16} />
                    Add Step
                  </Button>
                </div>
                <DataTable
                  columns={stepColumns}
                  rows={steps}
                  loading={false}
                  emptyState={<EmptyState title="No steps" description="No investigation steps found." />}
                />
              </div>
            )}

            {activeTab === 'messages' && (
              <DataTable
                columns={messageColumns}
                rows={messages}
                loading={false}
                emptyState={<EmptyState title="No messages" description="No investigation messages found." />}
              />
            )}

            {activeTab === 'correlations' && (
              <DataTable
                columns={correlationColumns}
                rows={correlations}
                loading={false}
                emptyState={<EmptyState title="No correlations" description="No correlations found for this investigation." />}
              />
            )}

            {activeTab === 'findings' && (
              <>
                <FormSection title="Root Cause Summary" data-testid="investigation-section-root-cause">
                  <p className="text-sm whitespace-pre-wrap">
                    {investigation.rootCauseSummary || '—'}
                  </p>
                </FormSection>

                <FormSection title="Remediation Steps" data-testid="investigation-section-remediation">
                  <p className="text-sm whitespace-pre-wrap">
                    {investigation.remediationSteps || '—'}
                  </p>
                </FormSection>

                <FormSection title="Prevention Recommendations" data-testid="investigation-section-prevention">
                  <p className="text-sm whitespace-pre-wrap">
                    {investigation.preventionRecommendations || '—'}
                  </p>
                </FormSection>
              </>
            )}
          </>
        )}
      </PageContainer>

      {id && (
        <InvestigationStepForm
          open={addStepOpen}
          investigationId={id}
          defaultOrder={steps.length + 1}
          onClose={() => setAddStepOpen(false)}
          onCreated={loadAll}
        />
      )}
    </>
  );
};
